/**
 * Button — the shared primitive that replaces inline BTN / BTNP / BTND
 * styles throughout the app. Token-driven so dark mode + future
 * re-skinning works for free.
 *
 * Variants:
 *   default     — neutral border + body text (was BTN)
 *   primary     — soft-blue with info accent (was BTNP)
 *   destructive — soft-red with danger accent (was BTND)
 *   ai          — solid purple (the AI Summary "Generate" button)
 *   ghost       — transparent, low emphasis
 *
 * Sizes:
 *   sm  — 4px 10px
 *   md  — 7px 14px (default)
 *   icon — square, single-character buttons
 */
const VARIANTS = {
  default: {
    background: 'var(--border)',
    color: 'var(--text-body)',
    border: '1px solid var(--border-soft)',
  },
  primary: {
    background: 'var(--info-soft)',
    color: 'var(--info)',
    border: '1px solid var(--info-border)',
  },
  destructive: {
    background: 'var(--danger-soft)',
    color: 'var(--danger)',
    border: '1px solid var(--danger-border)',
  },
  ai: {
    background: 'var(--ai)',
    color: '#fff',
    border: '1px solid var(--ai)',
  },
  'ai-soft': {
    background: 'var(--ai-soft)',
    color: 'var(--ai-text)',
    border: '1px solid var(--ai-border)',
  },
  ghost: {
    background: 'transparent',
    color: 'var(--text-muted)',
    border: '1px solid transparent',
  },
};

const SIZES = {
  sm:   { padding: '4px 10px', fontSize: 11 },
  md:   { padding: '7px 14px', fontSize: 12 },
  icon: { padding: '5px 8px',  fontSize: 12, lineHeight: 1 },
};

export default function Button({
  variant = 'default',
  size = 'md',
  fullWidth = false,
  disabled = false,
  style,
  children,
  ...props
}) {
  const v = VARIANTS[variant] || VARIANTS.default;
  const s = SIZES[size] || SIZES.md;
  return (
    <button
      disabled={disabled}
      style={{
        ...v,
        ...s,
        borderRadius: 'var(--radius)',
        fontWeight: variant === 'ai' || variant === 'primary' ? 600 : 500,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
        width: fullWidth ? '100%' : undefined,
        transition: 'background 0.12s, border-color 0.12s, color 0.12s',
        whiteSpace: 'nowrap',
        ...style,
      }}
      {...props}
    >
      {children}
    </button>
  );
}
